import Axios from 'axios';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { Store } from '../../Store';
import { getError } from '../../utils';
import { api } from '../../Api';
import LoadingBox from '../../component/LoadingBox';
import MessageBox from '../../component/MessageBox';

/**
 * MONEY CONTROLS — the one screen that decides whether members can move money
 * and how much it costs them. Everything here is enforced server-side; this is
 * a view over the platform Config document, not a client-side courtesy.
 */

const EMPTY = {
  withdrawEnabled: true,
  withdrawDisabledMessage: '',
  minimumWithdraw: 0,
  maximumWithdraw: 0,
  withdrawCharge: 0,
  transferEnabled: true,
  transferDisabledMessage: '',
  minimumTransfer: 0,
  maximumTransfer: 0,
  transferCharge: 0,
  convertEnabled: true,
  convertDisabledMessage: '',
  minimumConvert: 0,
  maximumConvert: 0,
  convertCharge: 0,
  convertSourceShoppingEnabled: true,
  convertSourceMatchingEnabled: true,
  convertSourceGlobalEnabled: false,
  convertSourceIncomeGenerationEnabled: false,
  convertSourceRegisterEnabled: true,
  convertSourceReferenceEnabled: false,
  convertSourcePsiEnabled: true,
  convertSourceShoppingBalanceEnabled: true,
  shoppingCheckoutRequired: false,
};

// Every balance a member can convert FROM. The destination is never a
// choice — everything lands in Available Balance — so this only lists
// sources, each gated by its own switch on the Config document.
const CONVERT_SOURCES = [
  { field: 'convertSourceRegisterEnabled', label: 'Register Point' },
  { field: 'convertSourceShoppingEnabled', label: 'Shopping Point' },
  { field: 'convertSourceMatchingEnabled', label: 'Group Sales' },
  { field: 'convertSourceGlobalEnabled', label: 'Profit Incentive (PI)' },
  { field: 'convertSourceIncomeGenerationEnabled', label: 'Income Generation' },
  { field: 'convertSourceReferenceEnabled', label: 'Reference Point' },
  { field: 'convertSourcePsiEnabled', label: 'Profit Sharing Incentive (PSI)' },
  { field: 'convertSourceShoppingBalanceEnabled', label: 'Shopping Balance (→ Register Point)' },
];

export default function MoneyControlScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;

  const [form, setForm] = useState(EMPTY);
  const [effectiveWithdrawCharge, setEffectiveWithdrawCharge] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await Axios.get(`${api}/api/users/admin/moneypolicy`, {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      });
      const { effectiveWithdrawCharge: eff, ...policy } = data || {};
      setForm({ ...EMPTY, ...policy });
      setEffectiveWithdrawCharge(Number(eff) || 0);
      setError('');
    } catch (err) {
      setError(getError(err));
    } finally {
      setLoading(false);
    }
  }, [userInfo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // Numbers come out of an <input> as strings; keep the empty string editable
  // rather than snapping it back to 0 on every keystroke.
  const onNumber = (key) => (e) => {
    const cleaned = e.target.value.replace(/[^0-9.]/g, '');
    setField(key, cleaned);
  };

  const numeric = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  };

  const save = async () => {
    const payload = {
      withdrawEnabled: Boolean(form.withdrawEnabled),
      withdrawDisabledMessage: form.withdrawDisabledMessage || '',
      minimumWithdraw: numeric(form.minimumWithdraw),
      maximumWithdraw: numeric(form.maximumWithdraw),
      withdrawCharge: numeric(form.withdrawCharge),
      transferEnabled: Boolean(form.transferEnabled),
      transferDisabledMessage: form.transferDisabledMessage || '',
      minimumTransfer: numeric(form.minimumTransfer),
      maximumTransfer: numeric(form.maximumTransfer),
      transferCharge: numeric(form.transferCharge),
      convertEnabled: Boolean(form.convertEnabled),
      convertDisabledMessage: form.convertDisabledMessage || '',
      minimumConvert: numeric(form.minimumConvert),
      maximumConvert: numeric(form.maximumConvert),
      convertCharge: numeric(form.convertCharge),
      convertSourceShoppingEnabled: Boolean(form.convertSourceShoppingEnabled),
      convertSourceMatchingEnabled: Boolean(form.convertSourceMatchingEnabled),
      convertSourceGlobalEnabled: Boolean(form.convertSourceGlobalEnabled),
      convertSourceIncomeGenerationEnabled: Boolean(
        form.convertSourceIncomeGenerationEnabled
      ),
      convertSourceRegisterEnabled: Boolean(form.convertSourceRegisterEnabled),
      convertSourceReferenceEnabled: Boolean(form.convertSourceReferenceEnabled),
      convertSourcePsiEnabled: Boolean(form.convertSourcePsiEnabled),
      convertSourceShoppingBalanceEnabled: Boolean(form.convertSourceShoppingBalanceEnabled),
      shoppingCheckoutRequired: Boolean(form.shoppingCheckoutRequired),
    };

    if (
      payload.maximumWithdraw > 0 &&
      payload.minimumWithdraw > 0 &&
      payload.maximumWithdraw < payload.minimumWithdraw
    ) {
      toast.error('Maximum withdraw cannot be lower than the minimum withdraw.');
      return;
    }
    if (
      payload.maximumTransfer > 0 &&
      payload.minimumTransfer > 0 &&
      payload.maximumTransfer < payload.minimumTransfer
    ) {
      toast.error('Maximum transfer cannot be lower than the minimum transfer.');
      return;
    }
    if (
      payload.maximumConvert > 0 &&
      payload.minimumConvert > 0 &&
      payload.maximumConvert < payload.minimumConvert
    ) {
      toast.error('Maximum convert cannot be lower than the minimum convert.');
      return;
    }

    try {
      setSaving(true);
      const { data } = await Axios.put(
        `${api}/api/users/admin/moneypolicy`,
        payload,
        { headers: { Authorization: `Bearer ${userInfo.token}` } }
      );
      const { message, changed, effectiveWithdrawCharge: eff, ...policy } = data || {};
      setForm({ ...EMPTY, ...policy });
      setEffectiveWithdrawCharge(Number(eff) || 0);
      toast.success(
        changed && changed.length
          ? `Saved. Updated: ${changed.join(', ')}`
          : 'Saved. Nothing changed.'
      );
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setSaving(false);
    }
  };

  const chargeOverridden =
    Number(effectiveWithdrawCharge) !== Number(form.withdrawCharge);

  return (
    <div>
      <Helmet>
        <title>Money Controls</title>
      </Helmet>
      <Container fluid="lg" className="mb-4 pt-3">
        <div className="mb-3">
          <h4 className="mb-1 fw-semibold">Money Controls</h4>
          <div className="text-muted small">
            Turn member withdrawals and point transfers on or off, cap the
            amounts, and set the charge. Every rule here is enforced on the
            server for the website and the mobile app alike.
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-center">
            <LoadingBox />
          </div>
        ) : error ? (
          <MessageBox variant="danger">{error}</MessageBox>
        ) : (
          <>
            <Row className="g-3">
              {/* ------------------------------------------------ WITHDRAW -- */}
              <Col xs={12} lg={6}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Header className="bg-white d-flex align-items-center justify-content-between">
                    <span className="fw-semibold">Withdraw</span>
                    <Badge bg={form.withdrawEnabled ? 'success' : 'danger'}>
                      {form.withdrawEnabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </Card.Header>
                  <Card.Body>
                    <Form.Check
                      type="switch"
                      id="withdraw-enabled"
                      className="mb-3"
                      label="Members can request withdrawals"
                      checked={Boolean(form.withdrawEnabled)}
                      onChange={(e) => setField('withdrawEnabled', e.target.checked)}
                    />

                    {!form.withdrawEnabled && (
                      <Form.Group className="mb-3">
                        <Form.Label className="small text-muted mb-1">
                          Message shown to members
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          maxLength={300}
                          placeholder="Withdrawals are temporarily turned off. Please try again later."
                          value={form.withdrawDisabledMessage}
                          onChange={(e) =>
                            setField('withdrawDisabledMessage', e.target.value)
                          }
                        />
                      </Form.Group>
                    )}

                    <Row className="g-2">
                      <Col xs={6}>
                        <Form.Label className="small text-muted mb-1">
                          Minimum amount
                        </Form.Label>
                        <Form.Control
                          inputMode="decimal"
                          value={form.minimumWithdraw}
                          onChange={onNumber('minimumWithdraw')}
                        />
                        <div className="form-text">0 = no minimum</div>
                      </Col>
                      <Col xs={6}>
                        <Form.Label className="small text-muted mb-1">
                          Maximum amount
                        </Form.Label>
                        <Form.Control
                          inputMode="decimal"
                          value={form.maximumWithdraw}
                          onChange={onNumber('maximumWithdraw')}
                        />
                        <div className="form-text">0 = no maximum</div>
                      </Col>
                      <Col xs={12}>
                        <Form.Label className="small text-muted mb-1">
                          Withdraw charge (%)
                        </Form.Label>
                        <Form.Control
                          inputMode="decimal"
                          value={form.withdrawCharge}
                          onChange={onNumber('withdrawCharge')}
                        />
                        <div className="form-text">
                          Taken out of the amount requested — the member receives
                          the remainder.
                        </div>
                      </Col>
                    </Row>

                    {chargeOverridden && (
                      <Alert variant="warning" className="mt-3 mb-0 py-2 small">
                        Income Generation is currently overriding this: the rate
                        actually applied to withdrawals is{' '}
                        <strong>{effectiveWithdrawCharge}%</strong>. Change it on
                        the Income Generation screen, or the value you set here
                        will not take effect.
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* ------------------------------------------------ TRANSFER -- */}
              <Col xs={12} lg={6}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Header className="bg-white d-flex align-items-center justify-content-between">
                    <span className="fw-semibold">Point Transfer</span>
                    <Badge bg={form.transferEnabled ? 'success' : 'danger'}>
                      {form.transferEnabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </Card.Header>
                  <Card.Body>
                    <Form.Check
                      type="switch"
                      id="transfer-enabled"
                      className="mb-3"
                      label="Members can transfer points to their team"
                      checked={Boolean(form.transferEnabled)}
                      onChange={(e) => setField('transferEnabled', e.target.checked)}
                    />

                    {!form.transferEnabled && (
                      <Form.Group className="mb-3">
                        <Form.Label className="small text-muted mb-1">
                          Message shown to members
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          maxLength={300}
                          placeholder="Point transfers are temporarily turned off. Please try again later."
                          value={form.transferDisabledMessage}
                          onChange={(e) =>
                            setField('transferDisabledMessage', e.target.value)
                          }
                        />
                      </Form.Group>
                    )}

                    <Row className="g-2">
                      <Col xs={6}>
                        <Form.Label className="small text-muted mb-1">
                          Minimum amount
                        </Form.Label>
                        <Form.Control
                          inputMode="decimal"
                          value={form.minimumTransfer}
                          onChange={onNumber('minimumTransfer')}
                        />
                        <div className="form-text">0 = no minimum</div>
                      </Col>
                      <Col xs={6}>
                        <Form.Label className="small text-muted mb-1">
                          Maximum amount
                        </Form.Label>
                        <Form.Control
                          inputMode="decimal"
                          value={form.maximumTransfer}
                          onChange={onNumber('maximumTransfer')}
                        />
                        <div className="form-text">0 = no maximum</div>
                      </Col>
                      <Col xs={12}>
                        <Form.Label className="small text-muted mb-1">
                          Transfer charge (%)
                        </Form.Label>
                        <Form.Control
                          inputMode="decimal"
                          value={form.transferCharge}
                          onChange={onNumber('transferCharge')}
                        />
                        <div className="form-text">
                          Charged to the sender on top of the amount. The
                          recipient always receives the full amount. 0 = free.
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>

              {/* ------------------------------------------------- CONVERT -- */}
              <Col xs={12}>
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-white d-flex align-items-center justify-content-between">
                    <span className="fw-semibold">Point Conversion</span>
                    <Badge bg={form.convertEnabled ? 'success' : 'danger'}>
                      {form.convertEnabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </Card.Header>
                  <Card.Body>
                    <Form.Check
                      type="switch"
                      id="convert-enabled"
                      className="mb-3"
                      label="Members can convert balances into Available Balance"
                      checked={Boolean(form.convertEnabled)}
                      onChange={(e) => setField('convertEnabled', e.target.checked)}
                    />

                    {!form.convertEnabled && (
                      <Form.Group className="mb-3">
                        <Form.Label className="small text-muted mb-1">
                          Message shown to members
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          maxLength={300}
                          placeholder="Point conversion is temporarily turned off. Please try again later."
                          value={form.convertDisabledMessage}
                          onChange={(e) =>
                            setField('convertDisabledMessage', e.target.value)
                          }
                        />
                      </Form.Group>
                    )}

                    <Row className="g-2 mb-3">
                      <Col xs={6} md={4}>
                        <Form.Label className="small text-muted mb-1">
                          Minimum amount
                        </Form.Label>
                        <Form.Control
                          inputMode="decimal"
                          value={form.minimumConvert}
                          onChange={onNumber('minimumConvert')}
                        />
                        <div className="form-text">0 = no minimum</div>
                      </Col>
                      <Col xs={6} md={4}>
                        <Form.Label className="small text-muted mb-1">
                          Maximum amount
                        </Form.Label>
                        <Form.Control
                          inputMode="decimal"
                          value={form.maximumConvert}
                          onChange={onNumber('maximumConvert')}
                        />
                        <div className="form-text">0 = no maximum</div>
                      </Col>
                      <Col xs={12} md={4}>
                        <Form.Label className="small text-muted mb-1">
                          Convert charge (%)
                        </Form.Label>
                        <Form.Control
                          inputMode="decimal"
                          value={form.convertCharge}
                          onChange={onNumber('convertCharge')}
                        />
                        <div className="form-text">
                          Taken out of the amount converted — Available
                          Balance is credited with the remainder.
                        </div>
                      </Col>
                    </Row>

                    <Form.Label className="small text-muted mb-2 d-block">
                      Sources members may convert from
                    </Form.Label>
                    <Row className="g-2">
                      {CONVERT_SOURCES.map((source) => (
                        <Col xs={12} sm={6} lg={3} key={source.field}>
                          <div className="border rounded p-2 h-100 d-flex align-items-center justify-content-between">
                            <span className="small">{source.label}</span>
                            <Form.Check
                              type="switch"
                              id={`convert-source-${source.field}`}
                              checked={Boolean(form[source.field])}
                              onChange={(e) =>
                                setField(source.field, e.target.checked)
                              }
                            />
                          </div>
                        </Col>
                      ))}
                    </Row>
                    <div className="text-muted small mt-2">
                      Every conversion lands in Available Balance. Turning a
                      source off hides it on the member Convert screen.
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* ---------------------------------------- SHOP CHECKOUT -- */}
              <Col xs={12}>
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-white d-flex align-items-center justify-content-between">
                    <span className="fw-semibold">Shop Checkout</span>
                    <Badge bg={form.shoppingCheckoutRequired ? 'success' : 'secondary'}>
                      {form.shoppingCheckoutRequired ? 'Pay from Shopping Balance' : 'Order without payment'}
                    </Badge>
                  </Card.Header>
                  <Card.Body>
                    <Form.Check
                      type="switch"
                      id="shopping-checkout-required"
                      className="mb-2"
                      label="Members must pay for shop orders from their Shopping Balance"
                      checked={Boolean(form.shoppingCheckoutRequired)}
                      onChange={(e) => setField('shoppingCheckoutRequired', e.target.checked)}
                    />
                    <div className="text-muted small">
                      On: the order total is taken from Shopping Balance the
                      moment the member confirms, and an order the balance
                      cannot cover is refused. Deleting such an order returns
                      the money. Off: orders are placed unpaid, as before.
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <div className="d-flex gap-2 mt-3">
              <Button variant="dark" onClick={save} disabled={saving}>
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" /> Saving...
                  </>
                ) : (
                  'Save money controls'
                )}
              </Button>
              <Button
                variant="outline-secondary"
                onClick={fetchData}
                disabled={saving}
              >
                Reload
              </Button>
            </div>

            <div className="text-muted small mt-3">
              Turning an action off blocks it before any one-time code is sent,
              so a disabled withdrawal costs nothing in SMS.
            </div>
          </>
        )}
      </Container>
    </div>
  );
}
